import type * as zod from 'zod';

import { DUMMY_MODEL_SENTINEL } from '@sb/ai/constants';
import {
  createMappingFromHardwareKeyToEquipmentInfo,
  createRobotIdsMap,
  getModelFromPath,
} from '@sb/ai/helpers';
import type { BotHardware, ParsedModel } from '@sb/ai/types';
import {
  cartesianPoseToMatrix4,
  customBaseRotationToQuaternion,
} from '@sb/geometry';
import { normalizeCameraConfigurationProto } from '@sb/integrations/implementations/Camera/implementation/normalize';
import type {
  CameraConfiguration,
  CameraState,
} from '@sb/integrations/implementations/Camera/types';
import type { GPUTaskRunner } from '@sb/integrations/implementations/GPUTaskRunner/implementation/GPUTaskRunner';
import type { GPUTaskRunnerState } from '@sb/integrations/implementations/GPUTaskRunner/types';
import type { Robotiq2FConfiguration } from '@sb/integrations/implementations/robotiq/Robotiq2F/types';
import type { SchunkEGxConfiguration } from '@sb/integrations/implementations/schunk/SchunkEGx/types/SchunkEGxConfiguration';
import type { StandardBotsRO1 } from '@sb/integrations/implementations/StandardBotsRO1/implementation/StandardBotsRO1';
import type { StandardBotsRO1State } from '@sb/integrations/implementations/StandardBotsRO1/types';
import type { EquipmentItem } from '@sb/integrations/types';
import * as log from '@sb/log';
import type { DeviceInfo } from '@sb/messenger/auto_generated/messenger/skills_bot_service';
import { CameraBotConnector } from '@sb/messenger/connectors/generated/CameraBotConnector';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import type { StepFailure } from '@sb/routine-runner/types';
import { formatImuCollisionMessage } from '@sb/routine-runner/types';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';
import { EventEmitter, wait } from '@sb/utilities';

import type { StepAction, StepPlayArguments } from '../Step';
import Step from '../Step';

import Arguments from './Arguments';
import { resolveCameraStreamId } from './resolveCameraStreamId';
import Variables from './Variables';
import type { CompletionReason } from './Variables';

const ns = log.namespace('RunSkillStep');

/**
 * Build a stringified robot config with tx_world_base auto-populated from the
 * enabled CustomBase equipment (if any).  When no CustomBase is configured,
 * the config is returned unchanged.
 */
export function buildRobotConfigJson(
  robotConfig: Record<string, unknown> | undefined,
  equipmentItems: EquipmentItem[],
): string {
  const config = robotConfig ? { ...robotConfig } : {};

  const customBase = equipmentItems.find(
    (item) => item.config.kind === 'CustomBase' && item.isEnabled,
  );

  if (
    customBase &&
    'rotation' in customBase.config &&
    'position' in customBase.config
  ) {
    const { position, rotation } = customBase.config as {
      position: { x: number; y: number; z: number };
      rotation: { roll: number; pitch: number; yaw: number };
    };

    const q = customBaseRotationToQuaternion(rotation);

    const matrix = cartesianPoseToMatrix4({
      x: position.x,
      y: position.y,
      z: position.z,
      ...q,
    });

    const e = matrix.elements;

    // Three.js stores elements column-major; convert to row-major 4×4
    const txWorldBase = [
      [e[0], e[4], e[8], e[12]],
      [e[1], e[5], e[9], e[13]],
      [e[2], e[6], e[10], e[14]],
      [e[3], e[7], e[11], e[15]],
    ];

    if (Array.isArray(config.robots)) {
      config.robots = (config.robots as Record<string, unknown>[]).map(
        (robot) => ({
          ...robot,
          tx_world_base: txWorldBase,
        }),
      );
    }
  }

  return JSON.stringify(config);
}

type Arguments = zod.infer<typeof Arguments>;
type Variables = zod.infer<typeof Variables>;

export default class RunSkillStep extends Step<Arguments, Variables> {
  private events = new EventEmitter<{
    stop: void;
    complete: { reason: CompletionReason };
    failure: StepFailure;
  }>();

  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  /**
   * @internal
   */
  private timeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Save any callbacks to cancel on destroy
   * @internal
   */
  private cancelers: Array<() => void> = [];

  private pauseReason: string | undefined;

  public initializeVariableState(): void {
    this.variables = {
      status: 'idle',
      lastCompletionReason: 'none',
    };
  }

  public async _setup(_args: StepPlayArguments): Promise<void> {
    const shouldRunPrimary = !this.args.isBimanual || this.args.isPrimary;

    if (!shouldRunPrimary) {
      return;
    }

    const gpuTaskRunner = this.getGpuTaskRunner();

    const gpuTaskRunnerEquipmentState = await gpuTaskRunner.updateAsync();

    if (!gpuTaskRunnerEquipmentState.isSkillsBotConnected) {
      throw new Error(
        `GPU Task runner ${gpuTaskRunner.getConfig().name} is not connected`,
      );
    }

    if (gpuTaskRunnerEquipmentState.isRunningInference) {
      throw new Error(
        `GPU Task runner ${gpuTaskRunner.getConfig().name}  is already running inference`,
      );
    }

    if (gpuTaskRunnerEquipmentState.isConfiguringInference) {
      throw new Error(
        `GPU Task runner ${gpuTaskRunner.getConfig().name}  is currently configuring inference`,
      );
    }

    if (gpuTaskRunnerEquipmentState.complianceFlagsMismatch) {
      throw new Error(
        'Compliance flags mismatch between CB and GPU Task Runner. Update feature flags on both machines to match.',
      );
    }

    if (this.args.modelPath.includes(DUMMY_MODEL_SENTINEL)) {
      // Dummy model: auto-build minimal equipment mapping
      // Only need robot_ids_map with primary robot; equipment_key_to_device_info
      // can be empty since the dummy model has no cameras
      const robotId = this.routineContext.robotId || 'local';
      const robotIdsMap: Record<string, string> = { robot_0: robotId };

      const gpuTaskRunnerConfig = gpuTaskRunner.getConfig();

      await gpuTaskRunner.executeCommand({
        kind: 'GPUTaskRunnerCommand',
        commandType: 'skill',
        subCommand: {
          command: 'configure_inference',
          modelName: this.args.modelPath,
          robotIdsMap,
          equipmentKeyToDeviceInfo: {},
          useGripperMask: false,
          robotConfig: buildRobotConfigJson(
            gpuTaskRunnerConfig.robot_config,
            this.routineContext.equipment.equipmentItems,
          ),
          obsHorizon: this.args.obsHorizon ?? 2,
          obsRes: [224, 224],
          robotControllerFrequency: this.args.robotControllerFrequency,
        },
      });

      return;
    }

    const allEquipmentState = this.routineContext.equipment.getEquipmentState();

    const robotIdsMap = createRobotIdsMap(
      this.args.hardwareConfigEquipmentMapping,
      this.routineContext.robotId || 'local',
      allEquipmentState,
    );

    const equipmentKeyToEquipmentInfo =
      createMappingFromHardwareKeyToEquipmentInfo(
        this.args.hardwareConfigEquipmentMapping,
      );

    const equipmentDeviceInfoEntries = await Promise.all(
      Object.entries(equipmentKeyToEquipmentInfo).map(
        async ([key, equipmentInfo]) =>
          [
            key,
            await this.equipmentInfoToDeviceData({
              equipmentInfo,
              robotIdsMap,
              hardwareKey: key,
            }),
          ] as const,
      ),
    );

    const equipmentDeviceInfo: { [key: string]: DeviceInfo } =
      Object.fromEntries(equipmentDeviceInfoEntries);

    // Inference configuration from model metadata (populated at training time from architecture's inference config)
    const inferenceConfig =
      this.getCurrentModel()?.modelMetadata?.trainingRun?.modelArchitecture
        ?.inferenceConfig;

    const gpuTaskRunnerConfig = gpuTaskRunner.getConfig();

    await gpuTaskRunner.executeCommand({
      kind: 'GPUTaskRunnerCommand',
      commandType: 'skill',
      subCommand: {
        command: 'configure_inference',
        modelName: this.args.modelPath,
        robotIdsMap,
        equipmentKeyToDeviceInfo: equipmentDeviceInfo,
        useGripperMask: this.args.useGripperMask,
        // Inference service config (from GPU Task Runner equipment),
        // with tx_world_base auto-populated from CustomBase if present
        robotConfig: buildRobotConfigJson(
          gpuTaskRunnerConfig.robot_config,
          this.routineContext.equipment.equipmentItems,
        ),
        obsHorizon: this.args.obsHorizon, // set from model metadata, if absent then editable in UI
        obsRes: inferenceConfig?.obsRes,
        robotControllerFrequency: this.args.robotControllerFrequency,
      },
    });
  }

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    if (this.routineContext.robot.robotKind !== 'live') {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'RunLearnedModel',
        },
        failureReason: 'Run skill step not supported in visualizer',
      });
    }

    const shouldRunPrimary = !this.args.isBimanual || this.args.isPrimary;

    // A loop replays the same instance; the completion latch must re-arm so
    // this iteration can complete.
    this.completionFired = false;

    try {
      if (shouldRunPrimary) {
        await this._playPrimary();
      } else {
        await this._playSecondary();
      }

      const completionPromise = this.events.next('complete').then((event) => ({
        type: 'complete' as const,
        reason: event.reason,
      }));

      const failurePromise = this.events.next('failure').then((failure) => ({
        type: 'failure' as const,
        failure,
      }));

      const stopPromise = this.events.next('stop').then(() => ({
        type: 'stopped' as const,
      }));

      await this._resume();

      const outcome = await Promise.race([
        completionPromise,
        failurePromise,
        stopPromise,
      ]);

      if (outcome.type === 'stopped') {
        // Step was stopped, exit gracefully
        return;
      }

      if (outcome.type === 'failure') {
        this.setVariable('status', 'failed');

        await this.conditionallyAddRecordingTag('inference-failure');

        await fail(outcome.failure);

        return;
      }

      this.setVariable('status', 'completed');
    } catch (error) {
      this.setVariable('status', 'failed');

      const fallbackMessage = `Failed to run skill: ${(error as Error)?.message}`;

      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'RunLearnedModel',
        },
        failureReason: this.getFailureMessage(fallbackMessage),
        error: error as Error,
      });
    }
  }

  public async pause(args: StepAction): Promise<void> {
    log.warn(
      ns`pause`,
      'Pause on "run skill" step is not supported, failing step.',
    );

    // RunSkill doesn't support pause - emit failure with specific error message
    // This handles cases like IK errors where the robot can't reach a position
    this.pauseReason = args.reason;

    this.setVariable('status', 'failed');

    const failureMessage =
      this.pauseReason || // Show pause reason like collision or IK if available
      this.getFailureMessage(
        'Run skill step was interrupted with a pause', // fallback default message
      );

    this.events.emit('failure', {
      failure: {
        kind: FailureKind.StepPlayFailure,
        stepKind: 'RunLearnedModel',
      },
      failureReason: failureMessage,
    });

    await this._stop();
  }

  public async _stop() {
    await this._pause();

    this.cancelers.forEach((cancel) => cancel());
    this.events.emit('stop');
  }

  public async _pause(): Promise<void> {
    /*
     * Internal function to stop inference.
     * Releases control source, then stops primary/secondary skills-bot inference.
     */
    await this.releaseControlSource();

    if (this.args.isPrimary) {
      await this._stopPrimary();
    }

    if (this.args.isBimanual && !this.args.isPrimary) {
      this._pauseSecondary();
    }

    await this.routineContext.stopGripper();

    if (this.timeout !== null) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  public async _resume(): Promise<void> {
    this.setVariable('status', 'running');
    this.setVariable('lastCompletionReason', 'none');

    if (!this.args.isBimanual || this.args.isPrimary) {
      await this._resumePrimary();
    } else {
      await this._resumeSecondary();
    }
  }

  /**
   * No step self timeout for this step: we already handle step timeouts within the step and we know that it will take variable amount of time - potentially many minutes.
   * @returns false
   */
  public hasSelfTimeout(): boolean {
    return false;
  }

  /**
   * Helpers
   */

  private async _playPrimary() {
    if (this.args.isBimanual) {
      const bots = this.getSecondaryBotEquipments();

      await this.waitForSecondaryBotsReady(bots);

      try {
        await Promise.all(
          bots.map((bot) =>
            bot.routineEditorVariablesApi.routineEditorVariablesUpdate(
              'inferenceRun',
              {
                value: true,
              },
            ),
          ),
        );
      } catch (error) {
        log.info(
          ns`inference.secondary`,
          'Failed to start secondary robot(s) inference',
          error,
        );
      }
    }

    const gpuTaskRunner = this.getGpuTaskRunner();

    const gpuTaskRunnerEquipmentState = await gpuTaskRunner.updateAsync();

    if (!gpuTaskRunnerEquipmentState.isSkillsBotConnected) {
      throw new Error(
        `GPU Task runner ${gpuTaskRunner.getConfig().name} is not connected`,
      );
    }

    if (gpuTaskRunnerEquipmentState.isRunningInference) {
      throw new Error(
        `GPU Task runner ${gpuTaskRunner.getConfig().name}  is already running inference`,
      );
    }

    if (gpuTaskRunnerEquipmentState.isConfiguringInference) {
      throw new Error(
        `GPU Task runner ${gpuTaskRunner.getConfig().name}  is currently configuring inference`,
      );
    }

    await gpuTaskRunner.executeCommand({
      kind: 'GPUTaskRunnerCommand',
      commandType: 'skill',
      subCommand: {
        command: 'start_inference',
        timeout: this.args.timeout,
        modelName: this.args.modelPath,
        completionPrediction: this.args.completionPrediction,
        caption: this.args.caption,
        frequency: this.args.frequency,
        stepsPerInference: this.args.stepsPerInference,
        maxLinearSpeed: this.args.maxLinearSpeed,
        actionExecutionMode: this.args.actionExecutionMode,
        actionInterpolationMethod: this.args.actionInterpolationMethod,
        outputSmoothingAlpha: this.args.outputSmoothingAlpha,
        enablePredictionLogging: this.args.enablePredictionLogging,
        lockedAxesPerRobot: this.args.lockedAxesPerRobot,
        axisLockingFramePerRobot: this.args.axisLockingFramePerRobot,
      },
    });
  }

  private async _playSecondary() {
    this.setRunSkillVariableState('inferenceRun', false);
  }

  private async _stopPrimary() {
    try {
      const gpuTaskRunner = this.getGpuTaskRunner();

      await gpuTaskRunner.executeCommand({
        kind: 'GPUTaskRunnerCommand',
        commandType: 'skill',
        subCommand: {
          command: 'stop_inference',
        },
      });
    } catch (error) {
      log.warn(
        ns`gpuTaskRunner.stopInference`,
        'Failed to stop inference on GPU Task Runner',
        error,
      );
    }

    if (this.args.isBimanual) {
      try {
        const bots = this.getSecondaryBotEquipments();

        if (bots.length === 0) {
          throw new Error('Secondary robot(s) not connected');
        }

        bots.forEach((bot) =>
          bot.routineEditorVariablesApi.routineEditorVariablesUpdate(
            'inferenceRun',
            {
              value: false,
            },
          ),
        );
      } catch (error) {
        log.info(
          ns`inference.secondary`,
          'Failed to stop secondary robot(s) inference',
          error,
        );
      }
    }
  }

  private async _pauseSecondary() {
    this.setRunSkillVariableState('inferenceReady', false);
  }

  /**
   * True once any completion path has fired for the current play. The three
   * paths (timeout, stop-condition poll, inference-status monitoring) each
   * guard with LOCAL flags only, so two can race through their own guards in
   * the same tick — this class-level latch makes the first reason win and
   * turns the loser into a no-op instead of an overwritten
   * `lastCompletionReason` and a double 'complete' emission. Reset at the top
   * of `_play` so loop replays of the same instance can complete again.
   * @internal
   */
  private completionFired = false;

  private complete(reason: CompletionReason) {
    if (this.completionFired) {
      return;
    }

    this.completionFired = true;
    this.setVariable('lastCompletionReason', reason);
    this.events.emit('complete', { reason });
  }

  private async _resumePrimary() {
    await this.setControlSource();

    await this.conditionallyAddRecordingTag('inference');

    // Add the training run ID as a tag if available
    const currentModel = this.getCurrentModel();
    const trainingRunId = currentModel?.modelMetadata?.trainingRun?.id;

    if (trainingRunId) {
      await this.conditionallyAddRecordingTag(`trainingRunId:${trainingRunId}`);
    }

    this.setStepTimeout();

    // Monitor inference status for all completion types (error, learned completion/failure, movement)
    this.setStepInferenceStatusMonitoring();

    this.setStepStopCondition();
  }

  private async _resumeSecondary() {
    this.setRunSkillVariableState('inferenceReady', true);
    this.setRunSkillVariableState('inferenceRun', false);

    let stopped = false;

    this.events.once('stop', () => {
      stopped = true;
    });

    const inferenceRunCancel = this.routineContext.onVariablesChange(
      (runVariables) => {
        if (
          runVariables.inferenceRun?.stepKind === 'RunLearnedModel' &&
          runVariables.inferenceRun?.value === true
        ) {
          this.setControlSource().catch((error) => {
            const fallbackMessage = `Failed to set control source: ${error instanceof Error ? error.message : String(error)}`;

            const stepFailure: StepFailure = {
              failure: {
                kind: FailureKind.InferenceFailure,
              },
              failureReason: this.getFailureMessage(fallbackMessage),
              error: error instanceof Error ? error : new Error(String(error)),
            };

            this.events.emit('failure', stepFailure);
          });

          // Now watch for primary bot to tell us to stop.
          const inferenceStopCancel = this.routineContext.onVariablesChange(
            (stopVariables) => {
              if (
                stopVariables.inferenceRun?.stepKind === 'RunLearnedModel' &&
                stopVariables.inferenceRun?.value === false
              ) {
                if (stopped) {
                  return;
                }

                this.complete('primaryStop');
              }
            },
          );

          this.cancelers.push(inferenceStopCancel);
        }
      },
    );

    this.cancelers.push(inferenceRunCancel);

    // Handle race condition: if primary already set inferenceRun=true before
    // we registered the listener, check current value and set control source now.
    // Read from this step's own frame, not the loaded routine's bag — the
    // bimanual sync pair lives in the same frame as this step.
    const currentVariables = this.routineFrame.variableValues;
    const inferenceRunVariables = currentVariables.inferenceRun;

    if (
      inferenceRunVariables?.stepKind === 'RunLearnedModel' &&
      inferenceRunVariables.value === true
    ) {
      await this.setControlSource();
    }
  }

  public async _teardown(_args: StepAction): Promise<void> {
    const shouldRunPrimary = !this.args.isBimanual || this.args.isPrimary;

    if (!shouldRunPrimary) {
      return;
    }

    const gpuTaskRunner = this.getGpuTaskRunner();

    await gpuTaskRunner.executeCommand({
      kind: 'GPUTaskRunnerCommand',
      subCommand: {
        command: 'teardown_inference',
      },
      commandType: 'skill',
    });
  }

  private async waitForSecondaryBotsReady(bots: StandardBotsRO1[]) {
    while (true) {
      try {
        if (bots.length === 0) {
          throw new Error('Not connected');
        }

        const allBotsReady = await Promise.all(
          bots.map(async (bot) => {
            const variable =
              await bot.routineEditorVariablesApi.routineEditorVariablesLoad(
                'inferenceReady',
              );

            return JSON.parse(variable.data.value);
          }),
        );

        if (allBotsReady.every((ready) => ready)) {
          break;
        }
      } catch (error) {
        log.info(
          ns`inference.secondary`,
          'Secondary robot(s) inference not ready yet',
          error,
        );
      }

      await wait(100);
    }
  }

  private setStepTimeout() {
    let stopped = false;
    let completed = false;

    this.events.once('stop', () => {
      stopped = true;
    });

    this.events.once('complete', () => {
      completed = true;
    });

    this.timeout = setTimeout(async () => {
      if (stopped || completed) {
        return;
      }

      if (this.args.isBimanual && this.args.isPrimary) {
        try {
          const bots = this.getSecondaryBotEquipments();

          if (bots.length === 0) {
            throw new Error('Secondary robot(s) not connected');
          }

          bots.forEach((bot) =>
            bot.routineEditorVariablesApi.routineEditorVariablesUpdate(
              'inferenceRun',
              {
                value: false,
              },
            ),
          );
        } catch (error) {
          log.info(
            ns`inference.secondary`,
            'Failed to stop secondary robot(s) inference',
            error,
          );
        }
      }

      await this.conditionallyAddRecordingTag('inference-timeout');

      this.complete('timeout');
    }, this.args.timeout * 1000);
  }

  private setStepStopCondition() {
    if (!this.args.condition) {
      log.info(
        ns`setStepStopCondition`,
        'No condition provided, using timeout only',
      );

      return;
    }

    const { condition } = this.args;

    // start thread for conditional monitoring every 100ms, exit step if condition is met
    this.startConditionalMonitoring(
      condition,
      async ({ isStopped }) => {
        // onConditionMet callback
        if (isStopped()) {
          return;
        }

        log.info(ns`setStepStopCondition`, `Stop condition met, stopping.`, {
          condition: JSON.stringify(condition),
        });

        // Release control, stop inference, stop gripper, and clear timeout
        await this._pause();

        await this.conditionallyAddRecordingTag('inference-stop-condition');

        this.complete('stopCondition');
      },
      (failure, { isStopped }) => {
        // onFailure callback
        if (isStopped()) {
          return;
        }

        this.events.emit('failure', failure);
      },
      { pollIntervalMs: 100 },
    );
  }

  /**
   * Unified monitoring for all inference status changes.
   * Monitors for: error, learnedCompletion, learnedFailure, movementCompletion
   */
  private setStepInferenceStatusMonitoring() {
    const gpuTaskRunner = this.getGpuTaskRunner();
    const cp = this.args.completionPrediction;

    let hasSeenInferenceRunning = false;

    // Map of inference status to completion reason and recording tag
    const statusConfig: Record<
      string,
      {
        completionReason: CompletionReason;
        tag: string;
        enabled: boolean;
        isError?: boolean;
      }
    > = {
      error: {
        completionReason: 'learnedFailure', // Not used for errors, but needed for type
        tag: 'inference-error',
        enabled: true, // Always monitor for errors
        isError: true,
      },
      completed: {
        completionReason: 'learnedCompletion',
        tag: 'inference-learned-completion',
        enabled: cp?.learnedCompletion?.enabled ?? false,
      },
      failed: {
        completionReason: 'learnedFailure',
        tag: 'inference-learned-failure',
        enabled: cp?.learnedCompletion?.enabled ?? false, // learnedFailure is tied to learnedCompletion flag
      },
      movementCompletion: {
        completionReason: 'movementCompletion',
        tag: 'inference-movement-completion',
        enabled: cp?.movementCompletion?.enabled ?? false,
      },
    };

    // TODO: Add OnRobot Hex-E disconnect monitoring (see teleop.ts hexEDisconnectListenerCancels)

    this.startMonitoring(
      async () => {
        const state = await gpuTaskRunner.updateAsync();

        // Check for error status immediately - can occur at any time
        if (state.inferenceStatus === 'error') {
          const errorReason =
            state.inferenceFailureDetails || 'Inference error occurred';

          return {
            triggered: true,
            context: {
              status: 'error' as const,
              errorReason,
            },
          };
        }

        // Track when inference has started
        if (state.isRunningInference) {
          hasSeenInferenceRunning = true;

          return { triggered: false };
        }

        // Only check completion statuses after inference has started and stopped
        if (hasSeenInferenceRunning) {
          const status = state.inferenceStatus;
          const config = statusConfig[status];

          if (config && config.enabled && !config.isError) {
            return {
              triggered: true,
              context: {
                status,
                completionReason: config.completionReason,
                tag: config.tag,
              },
            };
          }
        }

        return { triggered: false };
      },
      {
        onTriggered: async ({ isStopped }, context) => {
          if (isStopped()) {
            return;
          }

          // Clear the step timeout synchronously before awaiting pause operations
          if (this.timeout !== null) {
            clearTimeout(this.timeout);
            this.timeout = null;
          }

          // Release control source, stop inference, stop gripper, and clear timeout.
          await this._pause();

          const { status, tag } = context as {
            status: string;
            tag?: string;
            completionReason?: CompletionReason;
            errorReason?: string;
          };

          // Handle error status (emit failure)
          if (status === 'error') {
            const errorReason =
              (context as { errorReason?: string }).errorReason ||
              'Inference error occurred';

            log.error(
              ns`inferenceStatus`,
              'Inference error detected, stopping inference.',
              { errorReason },
            );

            await this.conditionallyAddRecordingTag('inference-error');

            this.events.emit('failure', {
              failure: {
                kind: FailureKind.InferenceFailure,
              },
              failureReason: this.getFailureMessage(errorReason),
            });

            return;
          }

          // Handle completion statuses
          const { completionReason } = context as {
            completionReason: CompletionReason;
          };

          log.info(
            ns`inferenceStatus`,
            `Inference status change detected: ${status} -> ${completionReason}`,
          );

          if (tag) {
            await this.conditionallyAddRecordingTag(tag);
          }

          this.complete(completionReason);
        },
        onError: (error, { isStopped }) => {
          if (isStopped()) {
            return;
          }

          log.error(
            ns`inferenceStatus`,
            'Error monitoring inference status',
            error,
          );
        },
      },
      { pollIntervalMs: 100 },
    );
  }

  private getSecondaryBotEquipments(): StandardBotsRO1[] {
    const connectedBots = this.routineContext.equipment.getDevicesByType(
      'StandardBotsRO1',
    ) as StandardBotsRO1[];

    return connectedBots.filter((bot) => bot.update().connected);
  }

  private getGpuTaskRunner(): GPUTaskRunner {
    const equipment = this.routineContext.equipment.getDeviceById(
      this.args.gpuTaskRunnerId,
    );

    if (equipment === undefined) {
      throw new Error(
        `Could not find GPU Task Runner equipment with id ${this.args.gpuTaskRunnerId}`,
      );
    }

    const gpuTaskRunner = equipment as GPUTaskRunner;

    return gpuTaskRunner;
  }

  private createCameraDeviceData(
    equipmentId: string,
    robotId: string,
    cameraConfig: CameraConfiguration,
    cameraConfigOptions: any,
    hardwareKey?: string,
  ): DeviceInfo {
    const botHardware = hardwareKey
      ? this.getBotHardwareForKey(hardwareKey)
      : undefined;

    const { streamId, frameType } = resolveCameraStreamId(
      cameraConfig,
      botHardware,
    );

    return {
      equipmentId,
      deviceId: cameraConfig.id,
      type: 'camera',
      robotId: this.getGpuTaskRunner().isLocal() ? 'local' : robotId,
      cameraConfig: normalizeCameraConfigurationProto(cameraConfigOptions) ?? {
        configOptions: {},
      },
      streamId,
      frameType,
    };
  }

  private createGripperDeviceData(
    equipmentId: string,
    robotId: string,
    gripperConfig: SchunkEGxConfiguration | Robotiq2FConfiguration,
  ): DeviceInfo {
    const deviceId =
      gripperConfig.kind === 'SchunkEGx'
        ? gripperConfig.variant
        : gripperConfig.modelVariant;

    return {
      equipmentId,
      robotId,
      deviceId,
      type: 'gripper',
    };
  }

  private createDeviceDataFromEquipmentKind(
    equipmentKind: string,
    equipmentId: string,
    robotId: string,
    equipmentConfig: any,
    cameraConfigOptions?: any,
    hardwareKey?: string,
  ): DeviceInfo {
    switch (equipmentKind) {
      case 'Camera':
        return this.createCameraDeviceData(
          equipmentId,
          robotId,
          equipmentConfig as CameraConfiguration,
          cameraConfigOptions,
          hardwareKey,
        );
      case 'SchunkEGx':
      case 'Robotiq2F':
        return this.createGripperDeviceData(
          equipmentId,
          robotId,
          equipmentConfig as SchunkEGxConfiguration | Robotiq2FConfiguration,
        );
      // TODO add other sensor types as needed
      default:
        throw new Error(`Unsupported equipment kind: ${equipmentKind}`);
    }
  }

  private async equipmentInfoToDeviceData({
    equipmentInfo,
    robotIdsMap,
    hardwareKey,
  }: {
    equipmentInfo: {
      robotKey: string;
      equipmentId: string;
    };
    robotIdsMap: Record<string, string>;
    hardwareKey?: string;
  }): Promise<DeviceInfo> {
    const { robotKey, equipmentId } = equipmentInfo;

    const robotId = robotIdsMap[robotKey];

    if (robotKey === 'robot_0') {
      const sensorEquipment =
        this.routineContext.equipment.getDeviceById(equipmentId);

      if (!sensorEquipment) {
        throw new Error(`Could not find equipment with id ${equipmentId}`);
      }

      const equipmentConfig = sensorEquipment.getConfig();

      const cameraConfigOptions =
        sensorEquipment.kind === 'Camera'
          ? (sensorEquipment.update() as CameraState)?.cameraDevice?.config
          : undefined;

      return this.createDeviceDataFromEquipmentKind(
        sensorEquipment.kind,
        equipmentId,
        robotId,
        equipmentConfig,
        cameraConfigOptions,
        hardwareKey,
      );
    }

    const secondaryBotsEquipmentId =
      this.args.hardwareConfigEquipmentMapping[robotKey].robotEquipmentId;

    if (!secondaryBotsEquipmentId) {
      throw new Error(`Could not find equipment with id ${equipmentId}`);
    }

    const secondaryBot = this.routineContext.equipment.getDeviceById(
      secondaryBotsEquipmentId,
    );

    if (secondaryBot == null) {
      throw new Error(
        `Could not find secondary bot equipment for robot key ${robotKey} (equipmentId: ${secondaryBotsEquipmentId})`,
      );
    }

    const secondaryBotState = secondaryBot.update() as StandardBotsRO1State;

    const secondaryBotEquipment = (
      secondaryBotState.equipmentConfig ?? []
    ).find((equipment) => equipment.id === equipmentId);

    if (secondaryBotEquipment == null) {
      throw new Error(
        `Could not find equipment on secondary bot (robotKey: ${robotKey}, equipmentId: ${equipmentId})`,
      );
    }

    const secondaryCameraConfig =
      secondaryBotEquipment.kind === 'Camera'
        ? (secondaryBotEquipment.config as CameraConfiguration)
        : undefined;

    let cameraConfigOptions = secondaryCameraConfig?.configuration;

    // The persisted equipmentConfig on the secondary bot is written at
    // registration time and isn't refreshed from the live camera-bot, so its
    // configOptions can drift from the actual sensor (e.g. width/height
    // reflecting BaseCamera defaults rather than what the GigE driver
    // resolved on connect). Live-query the secondary's camera-bot directly
    // so skills-bot's frame-shape assertions match what's actually streamed.
    if (secondaryCameraConfig) {
      try {
        const connector = new CameraBotConnector(
          this.routineContext.messengerNode,
          'camera-bot',
          robotId,
        );

        // camera-bot identifies cameras by their device id, not the
        // equipment-record id (`Camera.ts` uses `this.config.id` for the
        // primary's GetDevice call too).
        const observed = await connector.GetDevice(
          { value: secondaryCameraConfig.id },
          /* timeoutMs */ 2000,
        );

        if (observed.data?.config) {
          cameraConfigOptions = observed.data.config;
        }
      } catch (err) {
        log.warn(
          ns`equipmentInfoToDeviceData`,
          'Live GetDevice on secondary camera-bot failed; falling back to cached equipmentConfig',
          { robotId, equipmentId, cameraId: secondaryCameraConfig.id, err },
        );
      }
    }

    return this.createDeviceDataFromEquipmentKind(
      secondaryBotEquipment.kind,
      equipmentId,
      robotId,
      secondaryBotEquipment.config,
      cameraConfigOptions,
      hardwareKey,
    );
  }

  private getCurrentModel(): ParsedModel | undefined {
    const equipmentState = this.routineContext.equipment
      .getEquipmentState()
      .find((device) => device.id === this.args.gpuTaskRunnerId);

    if (!equipmentState) {
      return undefined;
    }

    const gpuTaskRunnerState = equipmentState.state as GPUTaskRunnerState;

    return getModelFromPath(gpuTaskRunnerState, this.args.modelPath);
  }

  private getBotHardwareForKey(hardwareKey: string): BotHardware | undefined {
    const currentModel = this.getCurrentModel();

    const inputConfig =
      currentModel?.modelMetadata?.trainingRun?.modelArchitecture
        ?.inputHardwareConfig?.config;

    if (!inputConfig) {
      return undefined;
    }

    const botHardwareMap = currentModel?.modelMetadata?.botHardware;

    if (!botHardwareMap) {
      return undefined;
    }

    // Find the hardware config for the given key
    for (const robotId of Object.keys(inputConfig.robots || {})) {
      const robot = inputConfig.robots[robotId];

      if (robot.hardware && robot.hardware[hardwareKey]) {
        const botHardwareId = robot.hardware[hardwareKey].id;

        return botHardwareMap[botHardwareId];
      }
    }

    return undefined;
  }

  private async setControlSource() {
    if (getFeatureFlagSync('useControlSourceManager', false)) {
      let source = 'ROS_POSE';

      const zmqPoseMigration = getFeatureFlagSync('zmqPoseMigration', false);
      const complianceMode = getFeatureFlagSync('complianceMode', false);

      if (complianceMode) {
        source = 'COMPLIANCE_JOINT_TRAJECTORY';
      } else if (zmqPoseMigration) {
        source = 'SKILLS_BOT';
      }

      const controlSourceRequest: { value: string; peerId?: string } = {
        value: source,
      };

      // Add peer_id when requesting SKILLS_BOT control
      // Use the GPU task runner's peer ID if not local
      if (source === 'SKILLS_BOT') {
        const gpuTaskRunner = this.getGpuTaskRunner();
        const peerId = gpuTaskRunner.getPeerId();

        if (peerId) {
          controlSourceRequest.peerId = peerId;
        }
      }

      await this.routineContext.armControlBotConnector?.SetControlSource(
        controlSourceRequest,
      );
    } else {
      throw new Error(
        'Control source manager is required but not enabled. Please enable the useControlSourceManager feature flag.',
      );
    }
  }

  private async releaseControlSource() {
    if (getFeatureFlagSync('useControlSourceManager', false)) {
      let source = 'ROS_POSE';

      const zmqPoseMigration = getFeatureFlagSync('zmqPoseMigration', false);
      const complianceMode = getFeatureFlagSync('complianceMode', false);

      if (complianceMode) {
        source = 'COMPLIANCE_JOINT_TRAJECTORY';
      } else if (zmqPoseMigration) {
        source = 'SKILLS_BOT';
      }

      await this.routineContext.armControlBotConnector?.ReleaseControlFromSource(
        {
          value: source,
        },
      );
    }
  }

  /**
   * Set a "runSkill" variable state. This is a bit of a hack.
   *
   * Currently, "RunSkill" has two variables (inferenceRun, inferenceReady). Unlike standard variables, these variables are not linked to a particular step ID within a routine. Instead, they operate somewhat similarly to an implicit routine variable. The main rationale behind this is that it makes it easy for the primary bot to send signals to the secondary bot. The main downside of this is a.) it muddies our variables state; b.) we don't have clear correspondence between steps across bots when running multiple skills. Stick with this for now in anticipation of a unified control box.
   *
   * @param name Name of variable to set
   * @param value Value to be set.
   */
  private setRunSkillVariableState(name: string, value: unknown) {
    this.routineFrame.setVariable(name, {
      stepKind: 'RunLearnedModel',
      name,
      value,
    });
  }

  /**
   * Get the failure message, preferring ControlSystemEvent and collision messages if available.
   * This step streams poses without using doMotion, so it doesn't receive
   * a MotionResult with error details. Instead, we check the robot's failure reason directly.
   * @param fallbackMessage - Message to use if no robot failure reason is available
   * @returns The failure message to display to the user
   */
  private getFailureMessage(fallbackMessage: string): string {
    const robotFailureReason = this.routineContext.robot.getFailureReason();

    if (robotFailureReason?.kind === 'controlSystemEvent') {
      return robotFailureReason.controlSystemEvent.message;
    }

    if (robotFailureReason?.kind === 'imuCollision') {
      return formatImuCollisionMessage(robotFailureReason);
    }

    if (robotFailureReason?.kind === 'collision') {
      return `Joint ${robotFailureReason.collision.joint} encountered a collision`;
    }

    return fallbackMessage;
  }
}
